/* ============================================================================
   global.js: site chrome interactions (header mobile menu, and the notice on
   the way from the merch store to the gear store).
   Cart + drawer live in cart.js; product variant logic in product.js.
   The footer newsletter is a native Shopify {% form 'customer' %} with
   server-rendered success/errors via Liquid, so it needs no JS.

   DOM contract:
     [data-menu-toggle]      header hamburger; toggles the mobile menu
     [data-mobile-menu]      collapsible panel (max-h-0 <-> max-h-96); `inert`
                             while collapsed so its links leave the tab order
     [data-menu-open-icon]   the "menu" glyph (visible when closed)
     [data-menu-close-icon]  the "x" glyph (visible when open)
     [data-menu-link]        links inside the mobile menu (click closes)
     [data-site-header]      the sticky chrome, measured into --header-h
     #cross-store-root       the notice (sections/cross-store-notice.liquid);
                             merch store only, so on the gear store it is absent
                             and nothing below runs
     [data-cross-store-url]  on the root: the gear store's address. A plain
                             click on a link to that host opens the notice
                             instead of following the link
     [data-cross-store-scrim]    backdrop; click closes
     [data-cross-store-panel]    the dialog (opacity-0 + pointer-events-none +
                                 `inert` while closed)
     [data-cross-store-continue] a real link, pointed at whatever was clicked,
                                 with that link's own target and rel
     [data-cross-store-close]    "Stay here"; closes
     shopify:section:* events    the theme editor's; the notice shows while its
                                 own section is the one chosen there
   ========================================================================== */
(function () {
  "use strict";

  /* ---- --header-h -------------------------------------------------------- */
  /* theme.css ships --header-h: 6.25rem, which is the announcement bar plus the
     nav row measured once by hand. It is the offset the collection filter bar
     sticks at and the scroll-padding in-page anchors land against, and it is a
     guess: the bar is a section setting a merchant can empty, and when they do
     the constant is about 26px too tall, so the filter bar hangs below the
     header with a strip of the page scrolling through the gap.

     Measured instead of guessed. The constant stays in theme.css as what the
     page uses before this runs and if it never does, so the worst case is
     today's behaviour rather than an unstyled one.

     The mobile menu is inside <header> and expands to 24rem, so the header's own
     height is not the answer - sticking the filter bar under an open menu would
     shove it a quarter of a screen down. Subtracting the panel gives the chrome
     that is actually pinned, and the panel is overflow-hidden at max-h-0 when
     closed, so the subtraction is zero in the ordinary case. */
  function measureHeader() {
    var header = document.querySelector("[data-site-header]");
    if (!header) return;
    var panel = header.querySelector("[data-mobile-menu]");
    var chrome = header.offsetHeight - (panel ? panel.offsetHeight : 0);
    if (chrome > 0) {
      document.documentElement.style.setProperty("--header-h", Math.round(chrome) + "px");
    }
  }

  measureHeader();
  if (window.ResizeObserver) {
    var header = document.querySelector("[data-site-header]");
    /* On the header, not the window: the bar wraps to two lines at some widths
       and the logo swaps size, and both change the height without a resize
       event of their own. Observing the element catches every cause at once. */
    if (header) new ResizeObserver(measureHeader).observe(header);
  } else {
    window.addEventListener("resize", measureHeader);
  }

  /* ---- header mobile menu ------------------------------------------------ */
  function setMenu(open) {
    var panel = document.querySelector("[data-mobile-menu]");
    var toggle = document.querySelector("[data-menu-toggle]");
    if (!panel || !toggle) return;
    panel.classList.toggle("max-h-96", open);
    panel.classList.toggle("max-h-0", !open);
    panel.toggleAttribute("inert", !open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    var openIcon = toggle.querySelector("[data-menu-open-icon]");
    var closeIcon = toggle.querySelector("[data-menu-close-icon]");
    if (openIcon) openIcon.classList.toggle("hidden", open);
    if (closeIcon) closeIcon.classList.toggle("hidden", !open);
  }

  /* ---- cross-store notice ------------------------------------------------ */
  /* Two storefronts exist so merch and RSR stock cannot land in one order, and
     the merch store's Kit item is a link into the other one: a different store
     with a different cart. The notice says so before the shopper arrives there
     wondering where their shirt went.

     Which clicks count is decided by where the link goes rather than by a mark
     on the link. The menu is the merchant's to edit in the admin, and a rule
     keyed to an item called Kit breaks the day it is renamed; a rule keyed to
     the gear store's host catches that item under any name, and every other
     link across as well. That includes the ones that open a new tab, the
     footer band and the kit buttons on the field notes: a tab alongside this
     one is still the other store's cart, and Continue opens that tab the way
     the link would have. Left alone is anything the shopper has asked for on
     purpose with a modifier or the middle button, and anything some other
     handler has already dealt with. */
  function noticeRoot() { return document.getElementById("cross-store-root"); }
  function noticePanel() { var r = noticeRoot(); return r && r.querySelector("[data-cross-store-panel]"); }
  function noticeScrim() { var r = noticeRoot(); return r && r.querySelector("[data-cross-store-scrim]"); }
  function noticeContinue() { var r = noticeRoot(); return r && r.querySelector("[data-cross-store-continue]"); }

  function hostOf(url) {
    try {
      return new URL(url, location.href).hostname.toLowerCase().replace(/^www\./, "");
    } catch (err) {
      return "";
    }
  }

  /* The host a click has to be headed for, or "" for none. An address that
     resolves to the store already on screen is refused rather than obeyed.
     snippets/store-url.liquid already writes the address whole, and blank at
     the host on screen, so this is a second check behind it: an address taken
     at its word that named this host would put the notice in front of every
     link on the site. */
  function gearHost() {
    var r = noticeRoot();
    var host = r ? hostOf(r.getAttribute("data-cross-store-url") || "") : "";
    return host && host !== hostOf(location.href) ? host : "";
  }

  function crossStoreLink(e) {
    if (e.defaultPrevented || e.button !== 0) return null;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
    var link = e.target.closest("a[href]");
    if (!link || link.closest("#cross-store-root") || link.hasAttribute("download")) return null;
    var host = gearHost();
    return host && hostOf(link.href) === host ? link : null;
  }

  // Element focus returns to when the notice closes (the link that opened it).
  var noticeFocus = null;

  // The same trap as the cart and search drawers: while the notice is open,
  // every top-level element except its own section is `inert`, so Tab cannot
  // reach the page behind it and assistive tech ignores it. Spared as well is
  // whatever holds [data-cart-status], cart.js's aria-live region, for the
  // reason cart.js gives.
  //
  // Written the same way in assets/cart.js and assets/search.js. They are three
  // copies of one trap over one global resource - document.body.children - and
  // none of them knows the others exist; an asset of its own to share eight
  // lines would cost more than it saves, so the duplication is deliberate and
  // this comment is the link.
  function noticeBackground() {
    var r = noticeRoot();
    var section = r && r.closest(".shopify-section");
    if (!section) return [];
    var status = document.querySelector("[data-cart-status]");
    return Array.prototype.filter.call(document.body.children, function (node) {
      return node !== section && !(status && node.contains(status));
    });
  }

  function noticeOpen() {
    var p = noticePanel();
    return !!p && !p.classList.contains("opacity-0");
  }

  function setNotice(open) {
    var p = noticePanel(), s = noticeScrim();
    if (!p || !s) return;
    s.classList.toggle("opacity-0", !open);
    s.classList.toggle("pointer-events-none", !open);
    p.classList.toggle("opacity-0", !open);
    p.classList.toggle("pointer-events-none", !open);
    document.body.classList.toggle("overflow-hidden", open);
    p.toggleAttribute("inert", !open);
    noticeBackground().forEach(function (node) { node.toggleAttribute("inert", open); });
  }

  // Whether it opened. A notice missing a part is a link left to work as a link:
  // the click is only cancelled once there is something on screen to answer it.
  function openNotice(link) {
    var go = noticeContinue();
    if (!noticePanel() || !noticeScrim() || !go) return false;
    noticeFocus = link;
    // Continue goes where the shopper was going, not to the gear store's front
    // door: a link to one of its collections should still arrive there. It goes
    // the way the link was going too, by taking the link's own target and rel:
    // a new-tab link still opens its new tab, noopener noreferrer and all, and a
    // same-tab one stays in this tab with its referrer. Continue is one link
    // reused for every trip, so a link without them takes off what the last one
    // put on.
    go.setAttribute("href", link.href);
    ["target", "rel"].forEach(function (name) {
      var value = link.getAttribute(name);
      if (value) go.setAttribute(name, value);
      else go.removeAttribute(name);
    });
    setNotice(true);
    go.focus();
    return true;
  }

  function closeNotice() {
    setNotice(false);
    if (noticeFocus && document.contains(noticeFocus) && typeof noticeFocus.focus === "function") {
      noticeFocus.focus();
    }
    noticeFocus = null;
  }

  document.addEventListener("click", function (e) {
    if (e.target.closest("[data-menu-toggle]")) {
      e.preventDefault();
      var panel = document.querySelector("[data-mobile-menu]");
      var isOpen = panel && panel.classList.contains("max-h-96");
      setMenu(!isOpen);
      return;
    }

    if (noticeOpen()) {
      if (e.target.closest("[data-cross-store-close]") || e.target === noticeScrim()) {
        e.preventDefault();
        closeNotice();
        return;
      }
      // Continue is a plain link and is followed as one. The notice and the menu
      // close behind it for the same reason menu links close the menu: a back-
      // button return to a cached page should not find either still hanging open,
      // and nor should the tab a new-tab Continue leaves behind.
      if (e.target.closest("[data-cross-store-continue]")) {
        closeNotice();
        setMenu(false);
        return;
      }
    }

    // Before the menu-link line below, and returning, so a Kit tap from the
    // mobile menu leaves the menu open under the notice. Closed, the menu is
    // `inert`, and "Stay here" could not hand focus back to the link it came from.
    var cross = crossStoreLink(e);
    if (cross && openNotice(cross)) {
      e.preventDefault();
      return;
    }

    if (e.target.closest("[data-menu-link]")) setMenu(false);
  });

  /* ---- the notice in the theme editor -------------------------------------- */
  /* Its words are boxes on its own section, the Gear site pop-up, and nothing
     opens it in the editor's preview, where a click is the editor's. So the
     notice shows while its section is the one chosen in the sidebar and hides
     once another is. A change to one of its boxes re-renders the section closed
     in place of the old one, which is why a load while it is chosen shows it
     again. Only the editor sends these events. */
  var editorShowing = false;

  function holdsNotice(e) {
    return !!(e.target && e.target.querySelector && e.target.querySelector("#cross-store-root"));
  }

  document.addEventListener("shopify:section:select", function (e) {
    if (holdsNotice(e)) {
      editorShowing = true;
      setNotice(true);
    } else if (editorShowing) {
      editorShowing = false;
      setNotice(false);
    }
  });

  document.addEventListener("shopify:section:deselect", function (e) {
    if (!holdsNotice(e) || !editorShowing) return;
    editorShowing = false;
    setNotice(false);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    if (holdsNotice(e) && noticeOpen()) setNotice(false);
  });

  document.addEventListener("shopify:section:load", function (e) {
    if (holdsNotice(e) && editorShowing) setNotice(true);
  });

  /* Escape closes whichever is on top: the notice if it is open, and only the
     notice, since a Kit tap from the mobile menu leaves the menu open beneath
     it. Otherwise the mobile menu, returning focus to the toggle. */
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    if (noticeOpen()) {
      closeNotice();
      return;
    }
    var panel = document.querySelector("[data-mobile-menu]");
    if (!panel || !panel.classList.contains("max-h-96")) return;
    setMenu(false);
    var toggle = document.querySelector("[data-menu-toggle]");
    if (toggle) toggle.focus();
  });
})();
